import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, X } from "lucide-react";

export default function Tracks() {
  const [tracks, setTracks] = useState([]);
  const [modules, setModules] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [editing, setEditing] = useState(null);
  const [assignTo, setAssignTo] = useState({});

  const load = async () => {
    const [t, m, e] = await Promise.all([api.get("/training/tracks"), api.get("/training/modules"), api.get("/employees")]);
    setTracks(t.data); setModules(m.data); setEmployees(e.data);
  };
  useEffect(() => { load(); }, []);

  const createNew = () => setEditing({ name: "New track", for_role: "", module_ids: [] });
  const save = async () => {
    if (!editing.name) return toast.warning("Name required");
    if (editing.track_id) await api.put(`/training/tracks/${editing.track_id}`, editing);
    else await api.post("/training/tracks", editing);
    toast.success("Saved"); setEditing(null); load();
  };

  const assign = async (trackId) => {
    const eid = assignTo[trackId];
    if (!eid) return toast.warning("Pick an employee");
    await api.post("/training/assign", { employee_id: eid, track_id: trackId });
    toast.success("Track assigned");
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Role tracks</h1>
          <p className="text-sm text-[#A1A1AA] mt-1">Ordered module playlists per role, reusable across people and teams.</p>
        </div>
        <button onClick={createNew} className="tt-btn-primary flex items-center gap-2"><Plus size={14} /> New track</button>
      </div>

      <div className="space-y-3">
        {tracks.map((t) => (
          <div key={t.track_id} className="tt-card p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="cursor-pointer" onClick={() => setEditing({ ...t })}>
                <div className="font-medium">{t.name}</div>
                <div className="text-xs font-mono text-[#71717A]">{t.for_role || "any role"} · {t.module_ids.length} modules</div>
              </div>
              <div className="flex gap-2 items-center">
                <select className="tt-input !w-auto text-xs" value={assignTo[t.track_id] || ""} onChange={(e) => setAssignTo({ ...assignTo, [t.track_id]: e.target.value })}>
                  <option value="">— employee —</option>
                  {employees.filter((e) => e.status !== "exited").map((e) => <option key={e.employee_id} value={e.employee_id}>{e.display_name || e.legal_name}</option>)}
                </select>
                <button onClick={() => assign(t.track_id)} className="tt-btn-secondary text-xs">Assign</button>
              </div>
            </div>
            <div className="flex gap-1 flex-wrap">
              {t.module_ids.map((mid) => {
                const m = modules.find((x) => x.module_id === mid);
                return <span key={mid} className="tt-badge-neutral">{m?.title || mid}</span>;
              })}
            </div>
          </div>
        ))}
        {tracks.length === 0 && <div className="text-sm text-[#71717A]">No tracks yet.</div>}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-[rgba(10,10,10,0.85)] flex items-center justify-center z-40 p-4" onClick={() => setEditing(null)}>
          <div className="w-full max-w-xl tt-card p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <input className="tt-input text-lg w-80" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
              <button onClick={() => setEditing(null)} className="tt-btn-ghost"><X size={16} /></button>
            </div>
            <input className="tt-input mb-4" placeholder="For role (e.g. Motion Designer)" value={editing.for_role || ""} onChange={(e) => setEditing({ ...editing, for_role: e.target.value })} />
            <div className="tt-label mb-1">Modules (in order)</div>
            <div className="space-y-1 mb-3">
              {editing.module_ids.map((mid, idx) => {
                const m = modules.find((x) => x.module_id === mid);
                return (
                  <div key={mid + idx} className="flex items-center gap-2 py-1 border-b border-[#1E1E1E]">
                    <span className="font-mono text-xs text-[#71717A] w-6">{idx + 1}</span>
                    <div className="flex-1">{m?.title || mid}</div>
                    <button onClick={() => { const n = editing.module_ids.filter((_, i) => i !== idx); setEditing({ ...editing, module_ids: n }); }} className="tt-btn-ghost text-[#F5A524] text-xs">Remove</button>
                  </div>
                );
              })}
            </div>
            <select className="tt-input" onChange={(e) => { if (!e.target.value) return; setEditing({ ...editing, module_ids: [...editing.module_ids, e.target.value] }); e.target.value = ""; }}>
              <option value="">— add module —</option>
              {modules.filter((m) => !editing.module_ids.includes(m.module_id)).map((m) => <option key={m.module_id} value={m.module_id}>{m.title}</option>)}
            </select>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setEditing(null)} className="tt-btn-secondary text-sm">Cancel</button>
              <button onClick={save} className="tt-btn-primary text-sm">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
